import { AxiosError } from 'axios';
import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../api/client';

type Project = {
  id: string;
  name: string;
};

type ProjectsListResponse = Project[] | { items: Project[] };

function extractProjects(data: ProjectsListResponse): Project[] {
  return Array.isArray(data) ? data : data.items;
}

function PencilIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

function getErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    if (error.response?.status === 401) {
      return 'Сессия истекла. Войдите в систему заново.';
    }

    if (error.response?.status === 404) {
      return 'Проект не найден.';
    }
  }

  return 'Не удалось выполнить операцию. Попробуйте ещё раз.';
}

export function ProjectsPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function loadProjects() {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const response = await apiClient.get<ProjectsListResponse>('/projects');
      setProjects(extractProjects(response.data));
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    void loadProjects();
  }, []);

  async function handleCreateProject(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const trimmedName = newProjectName.trim();
    if (!trimmedName) {
      setErrorMessage('Введите название проекта.');
      return;
    }

    setIsCreating(true);
    setErrorMessage(null);

    try {
      const response = await apiClient.post<Project>('/projects', { name: trimmedName });
      setProjects((currentProjects) => [response.data, ...currentProjects]);
      setNewProjectName('');
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsCreating(false);
    }
  }

  async function handleRenameProject(project: Project) {
    const nextName = window.prompt('Новое название проекта', project.name);
    if (nextName === null) {
      return;
    }

    const trimmedName = nextName.trim();
    if (!trimmedName) {
      setErrorMessage('Название не может быть пустым.');
      return;
    }

    if (trimmedName === project.name) {
      return;
    }

    setErrorMessage(null);

    try {
      const response = await apiClient.patch<Project>(`/projects/${project.id}`, {
        name: trimmedName,
      });

      setProjects((currentProjects) =>
        currentProjects.map((currentProject) =>
          currentProject.id === project.id ? response.data : currentProject,
        ),
      );
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  }

  async function handleDeleteProject(project: Project) {
    const confirmed = window.confirm(
      `Удалить проект "${project.name}"? Это действие нельзя отменить.`,
    );

    if (!confirmed) {
      return;
    }

    setErrorMessage(null);

    try {
      await apiClient.delete(`/projects/${project.id}`);
      setProjects((currentProjects) =>
        currentProjects.filter((currentProject) => currentProject.id !== project.id),
      );
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  }

  return (
    <main style={{ display: 'grid', gap: '16px' }}>
      <h1>Список проектов</h1>

      <form
        onSubmit={handleCreateProject}
        style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}
      >
        <input
          type="text"
          value={newProjectName}
          onChange={(event) => setNewProjectName(event.target.value)}
          placeholder="Название проекта"
          maxLength={120}
          required
          className="field"
        />
        <button type="submit" className="btn" disabled={isCreating}>
          {isCreating ? 'Создание...' : 'Создать проект'}
        </button>
      </form>

      {errorMessage ? (
        <p role="alert" style={{ color: '#b91c1c', margin: 0 }}>
          {errorMessage}
        </p>
      ) : null}

      {isLoading ? <p>Загружаем проекты...</p> : null}

      {!isLoading && projects.length === 0 ? <p>Проектов пока нет. Создайте первый проект.</p> : null}

      {!isLoading && projects.length > 0 ? (
        <ul style={{ display: 'grid', gap: '10px', listStyle: 'none', padding: 0, margin: 0 }}>
          {projects.map((project) => (
            <li
              key={project.id}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                background: '#fff',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
              }}
            >
              <div style={{ display: 'grid', gap: '4px' }}>
                <Link to={`/projects/${project.id}`} style={{ fontWeight: 600 }}>
                  {project.name}
                </Link>
                <small style={{ color: '#6b7280' }}>ID: {project.id}</small>
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  className="btn btn-icon"
                  aria-label="Переименовать"
                  title="Переименовать"
                  onClick={() => handleRenameProject(project)}
                >
                  <PencilIcon />
                </button>
                <button
                  type="button"
                  className="btn btn-icon btn-danger"
                  aria-label="Удалить"
                  title="Удалить"
                  onClick={() => handleDeleteProject(project)}
                >
                  <TrashIcon />
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </main>
  );
}
